import { CanvasSource, UPDATE_PRIORITY, type Ticker } from "pixi.js";

const browserTickers = new WeakMap<Ticker, { references: number; originalMaxFps: number }>();

/** Refresh only Browser textures when the preload has drawn a new GPU frame. */
export class BrowserGpuCanvasSource extends CanvasSource {
  private readonly canvas: HTMLCanvasElement;
  private readonly ticker?: Ticker;
  private dirty = false;
  private readonly onFrame = () => {
    this.dirty = true;
    if (!this.ticker) this.onTick();
  };
  private readonly onTick = () => {
    if (this.dirty) { this.dirty = false; this.update(); }
  };

  constructor(canvas: HTMLCanvasElement, ticker?: Ticker) {
    super({ resource: canvas });
    this.canvas = canvas;
    this.ticker = ticker;
    canvas.addEventListener("browser-capture-frame", this.onFrame);
    if (ticker) {
      ticker.add(this.onTick, this, UPDATE_PRIORITY.HIGH);
      const lease = browserTickers.get(ticker) ?? { references: 0, originalMaxFps: ticker.maxFPS };
      if (lease.references++ === 0 && ticker.maxFPS === 60) {
        // Pixi truncates elapsed milliseconds before its FPS gate. A 16.67ms
        // vsync therefore occasionally fails the 60fps gate. This small allowance
        // keeps the 60Hz composition paced by vsync, without uncapping fast displays.
        ticker.maxFPS = 61;
      }
      browserTickers.set(ticker, lease);
    }
  }

  override destroy() {
    if (this.destroyed) return;
    this.canvas.removeEventListener("browser-capture-frame", this.onFrame);
    if (this.ticker) {
      this.ticker.remove(this.onTick, this);
      const lease = browserTickers.get(this.ticker);
      if (lease && --lease.references === 0) {
        if (lease.originalMaxFps === 60 && this.ticker.maxFPS === 61) this.ticker.maxFPS = 60;
        browserTickers.delete(this.ticker);
      }
    }
    super.destroy();
  }
}
